import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, AlertCircle, Loader2, CheckCircle, Clock, User, Mail } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useDeal } from '../contexts/DealContext';
import { useDealScenarios } from '../hooks/useDealScenarios';

interface LOIDocument {
  id: string;
  deal_id: string;
  buyer_name: string;
  seller_name: string;
  buyer_email?: string;
  seller_email?: string;
  loi_type: string;
  status: string;
  purchase_price?: number;
  currency: string;
  due_diligence_days: number;
  exclusivity_days: number;
  closing_date?: string;
  terms_and_conditions?: string;
  special_conditions?: string;
  created_at: string;
  updated_at: string;
  has_buyer_signature: boolean;
  has_seller_signature: boolean;
  all_signatures_complete: boolean;
}

interface CreateLOIFormData {
  buyer_name: string;
  seller_name: string;
  buyer_email: string;
  seller_email: string;
  loi_type: 'standard' | 'option1' | 'option2';
  selected_deal_scenario_id?: string;
  purchase_price?: number;
  currency: string;
  due_diligence_days: number;
  exclusivity_days: number;
  closing_date?: string;
  terms_and_conditions?: string;
  special_conditions?: string;
}

export function DealLOIManager() {
  const { dealId, scenarioId } = useParams<{ dealId: string; scenarioId?: string }>();
  const { user } = useUser();
  const { selectedDeal, availableDeals, selectDeal } = useDeal();
  const { scenarios, loading: scenariosLoading } = useDealScenarios();
  const [loiDocuments, setLoiDocuments] = useState<LOIDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateLOIFormData>({
    buyer_name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
    seller_name: '',
    buyer_email: user?.emailAddresses?.[0]?.emailAddress || '',
    seller_email: '',
    loi_type: 'standard',
    currency: 'USD',
    due_diligence_days: 30,
    exclusivity_days: 30
  });

  // Handle deal selection when URL changes
  useEffect(() => {
    if (!dealId) return;
    
    // Check if the selected deal matches the URL parameter
    if (!selectedDeal || selectedDeal.deal_id !== dealId) {
      // Try to find the deal in available deals and select it
      const deal = availableDeals.find(d => d.deal_id === dealId);
      if (deal) {
        selectDeal(deal);
      }
    }
  }, [dealId, selectedDeal?.deal_id, availableDeals, selectDeal]);

  // Check if we have a valid deal selected
  if (!dealId) {
    return <Navigate to="/deals" replace />;
  }

  // Show loading state while deal is being selected
  if (!selectedDeal || selectedDeal.deal_id !== dealId) {
    const deal = availableDeals.find(d => d.deal_id === dealId);
    if (deal) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading deal...</p>
          </div>
        </div>
      );
    } else {
      return <Navigate to="/deals" replace />;
    }
  }

  // Filter displayed LOIs by selected scenario when provided
  const filteredLoiDocuments = React.useMemo(() => {
    if (!scenarioId) return loiDocuments;
    return loiDocuments.filter((d: any) => d.selected_deal_scenario_id === scenarioId);
  }, [loiDocuments, scenarioId]);

  // Load LOI documents for this deal
  useEffect(() => {
    loadLOIDocuments();
  }, [dealId]);

  const loadLOIDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await DatabaseService.getDealLOIDocuments(dealId!);
      if (response.data) {
        setLoiDocuments(response.data);
        // Automatically show create form if no LOI documents exist
        if (response.data.length === 0) {
          setShowCreateForm(true);
        }
      } else if (response.error) {
        setMessage(`Error loading LOI documents: ${response.error}`);
        // Show create form even if there's an error loading documents
        setShowCreateForm(true);
      }
    } catch (error) {
      setMessage(`Error loading LOI documents: ${error}`);
      // Show create form even if there's an error loading documents
      setShowCreateForm(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLOI = async () => {
    if (!createFormData.buyer_name || !createFormData.seller_name) {
      setMessage('Please fill in buyer and seller names');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!createFormData.selected_deal_scenario_id) {
      setMessage('Please select a deal scenario');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsCreating(true);
    try {
      const response = await DatabaseService.createLOIDocument(dealId!, createFormData);
      if (response.error) {
        setMessage(`Error creating LOI: ${response.error}`);
      } else {
        setMessage('LOI created successfully');
        setShowCreateForm(false);
        setCreateFormData({
          buyer_name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
          seller_name: '',
          buyer_email: user?.emailAddresses?.[0]?.emailAddress || '',
          seller_email: '',
          loi_type: 'standard',
          currency: 'USD',
          due_diligence_days: 30,
          exclusivity_days: 30
        });
        loadLOIDocuments();
      }
    } catch (error) {
      setMessage(`Error creating LOI: ${error}`);
    } finally {
      setIsCreating(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <Edit className="w-4 h-4 text-gray-500" />;
      case 'pending_review':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'pending_signature':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'signed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'executed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_signature':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'executed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FileText className="w-8 h-8 text-blue-600" />
          Letter of Intent - {selectedDeal.business_name}
        </h1>
        <p className="text-gray-600">
          Create and manage Letter of Intent documents for this business acquisition deal.
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{message}</p>
          </div>
        </div>
      )}

      {/* Create LOI Button - only show if no LOIs exist and user has write/admin access */}
      {loiDocuments.length === 0 && !isLoading && selectedDeal.access_type !== 'read' && (
        <div className="mb-6">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Letter of Intent
          </button>
        </div>
      )}

      {/* Create LOI Form - only show for write/admin users */}
      {showCreateForm && selectedDeal.access_type !== 'read' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Letter of Intent</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buyer Name *
              </label>
              <input
                type="text"
                value={createFormData.buyer_name}
                onChange={(e) => setCreateFormData({ ...createFormData, buyer_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Buyer's full name"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seller Name *
              </label>
              <input
                type="text"
                value={createFormData.seller_name}
                onChange={(e) => setCreateFormData({ ...createFormData, seller_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Seller's full name"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buyer Email
              </label>
              <input
                type="email"
                value={createFormData.buyer_email}
                onChange={(e) => setCreateFormData({ ...createFormData, buyer_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="buyer@example.com"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seller Email
              </label>
              <input
                type="email"
                value={createFormData.seller_email}
                onChange={(e) => setCreateFormData({ ...createFormData, seller_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="seller@example.com"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LOI Type
              </label>
              <select
                value={createFormData.loi_type}
                onChange={(e) => setCreateFormData({ ...createFormData, loi_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreating}
              >
                <option value="standard">Standard LOI</option>
                <option value="option1">Option 1 - $1.1M Deal Structure</option>
                <option value="option2">Option 2 - Interest-Only Structure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deal Scenario *
              </label>
              <select
                value={createFormData.selected_deal_scenario_id || ''}
                onChange={(e) => setCreateFormData({ ...createFormData, selected_deal_scenario_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreating || scenariosLoading}
                required
              >
                <option value="">Select a deal scenario...</option>
                {scenarios.map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.name} - ${scenario.headline_price?.toLocaleString() || '0'}
                  </option>
                ))}
              </select>
              {scenariosLoading && (
                <p className="text-sm text-gray-500 mt-1">Loading scenarios...</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purchase Price
              </label>
              <input
                type="number"
                value={createFormData.purchase_price || ''}
                onChange={(e) => setCreateFormData({ ...createFormData, purchase_price: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1800000"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Diligence Period (days)
              </label>
              <input
                type="number"
                value={createFormData.due_diligence_days}
                onChange={(e) => setCreateFormData({ ...createFormData, due_diligence_days: parseInt(e.target.value) || 30 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exclusivity Period (days)
              </label>
              <input
                type="number"
                value={createFormData.exclusivity_days}
                onChange={(e) => setCreateFormData({ ...createFormData, exclusivity_days: parseInt(e.target.value) || 30 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreating}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Conditions
              </label>
              <textarea
                value={createFormData.special_conditions || ''}
                onChange={(e) => setCreateFormData({ ...createFormData, special_conditions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Any special conditions or terms..."
                disabled={isCreating}
              />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button
                onClick={handleCreateLOI}
                disabled={isCreating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isCreating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create LOI
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                disabled={isCreating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOI Documents List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Letter of Intent Documents
          </h3>
          {loiDocuments.length > 0 && selectedDeal.access_type !== 'read' && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Another LOI
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading LOI documents...</span>
          </div>
        ) : loiDocuments.length > 0 ? (
          <div className="space-y-4">
            {loiDocuments.map((loi) => (
              <div
                key={loi.id}
                className="border rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {loi.loi_type === 'standard' ? 'Standard LOI' : 
                         loi.loi_type === 'option1' ? 'Option 1 - $1.1M Deal Structure' :
                         'Option 2 - Interest-Only Structure'}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(loi.status)}`}>
                        {getStatusIcon(loi.status)}
                        {loi.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Buyer:</strong> {loi.buyer_name}</p>
                        {loi.buyer_email && <p><strong>Email:</strong> {loi.buyer_email}</p>}
                      </div>
                      <div>
                        <p><strong>Seller:</strong> {loi.seller_name}</p>
                        {loi.seller_email && <p><strong>Email:</strong> {loi.seller_email}</p>}
                      </div>
                      {loi.purchase_price && (
                        <div>
                          <p><strong>Purchase Price:</strong> {formatCurrency(loi.purchase_price, loi.currency)}</p>
                        </div>
                      )}
                      <div>
                        <p><strong>Due Diligence:</strong> {loi.due_diligence_days} days</p>
                        <p><strong>Exclusivity:</strong> {loi.exclusivity_days} days</p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        {loi.has_buyer_signature ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400" />
                        )}
                        Buyer Signature
                      </span>
                      <span className="flex items-center gap-1">
                        {loi.has_seller_signature ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400" />
                        )}
                        Seller Signature
                      </span>
                      <span className="text-gray-500">
                        Created: {new Date(loi.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => window.open(`/deals/${dealId}/loi/${loi.id}`, '_blank')}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <FileText className="w-3 h-3" />
                      View
                    </button>
                    {loi.status === 'draft' && (
                      <button
                        onClick={() => {/* TODO: Implement edit functionality */}}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                        title="Edit LOI"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No Letter of Intent documents found for this deal.</p>
            <p className="text-sm mt-2">Create your first LOI to get started with the acquisition process.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
            >
              <Plus className="w-4 h-4" />
              Create LOI
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

